import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'

const LOCK_NAME = 'notificationSound'
const PLAYED_LOCK_PREFIX = 'notificationSound.played:'
const PLAYED_LOCK_HOLD_MS = 10 * 60 * 1000
const PLAYED_STORAGE_KEY = 'notificationSound.played'
const PLAYED_RETENTION_MS = 12 * 60 * 60 * 1000
const PLAY_TIMEOUT_MS = 3000
const HIDDEN_TAB_DELAY_MS = 500

type PlayedSounds = Record<string, number>

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

const readPlayed = (): PlayedSounds => {
  try {
    const stored = localStorage.getItem(PLAYED_STORAGE_KEY)
    return stored ? (JSON.parse(stored) as PlayedSounds) : {}
  } catch {
    return {}
  }
}

const writePlayed = (played: PlayedSounds, key: string, now: number) => {
  const kept = Object.fromEntries(Object.entries(played).filter(([, at]) => now - at < PLAYED_RETENTION_MS))
  try {
    localStorage.setItem(PLAYED_STORAGE_KEY, JSON.stringify({ ...kept, [key]: now }))
  } catch {
    // Without storage, only the held lock remembers the sound, for the tabs open now.
  }
}

// A tab the browser keeps in the background can leave play() pending, which would hold the lock and silence every
// other tab: past the timeout the tab stops its sound and lets the next tab play.
const playWithin = async (play: (signal: AbortSignal) => Promise<void>) => {
  const controller = new AbortController()
  const timer = setTimeout(() => {
    controller.abort(new DOMException('play() did not start in time', 'TimeoutError'))
  }, PLAY_TIMEOUT_MS)
  try {
    await Promise.race([
      play(controller.signal),
      new Promise<never>((_resolve, reject) => {
        controller.signal.addEventListener('abort', () => {
          reject(controller.signal.reason as Error)
        })
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

const isHeld = async (key: string) => {
  const { held = [] } = await navigator.locks.query()
  return held.some(({ name }) => name === `${PLAYED_LOCK_PREFIX}${key}`)
}

const holdPlayed = (key: string) =>
  new Promise<void>((resolve) => {
    handleNotAwaitedPromise(
      navigator.locks.request(`${PLAYED_LOCK_PREFIX}${key}`, async () => {
        resolve()
        await sleep(PLAYED_LOCK_HOLD_MS)
      })
    )
  })

// Every tab of the dashboard, and every Soutien frame in Pilote, receives the same event, and only one of them plays
// it. localStorage alone cannot decide: Chrome hands its writes to other tabs late, so the tab that plays also holds
// a lock named after the event, which the lock manager shows to every tab at once. A tab whose play() is refused (no
// user gesture yet) records nothing, so the next tab in line plays instead; a hidden tab queues last, since the
// browser may hold its sound back until it is shown.
export const playOnce = async (key: string, play: (signal: AbortSignal) => Promise<void>) => {
  if (!('locks' in navigator)) {
    await playWithin(play)
    return
  }
  if (document.visibilityState === 'hidden') {
    await sleep(HIDDEN_TAB_DELAY_MS)
  }
  await navigator.locks.request(LOCK_NAME, async () => {
    const played = readPlayed()
    if (played[key] !== undefined || (await isHeld(key))) {
      return
    }
    await playWithin(play)
    await holdPlayed(key)
    writePlayed(played, key, Date.now())
  })
}
